import { LENS_PERIOD_KEY, type LensFilterValues, type LensPeriod, type LensPeriodComparison } from '~/types/leLens';

import type { LensRange, LensSelection } from './cardView';
import { isLensPeriod } from './lensPeriodPresets';

/**
 * The part of a dashboard's state that belongs in its address.
 *
 * A dashboard is something people send each other, and "look at this board"
 * only means anything if the filters travel with the link. Everything here is
 * a viewer's own choice, so it lives in the URL rather than in the saved spec.
 */
export const LENS_VIEW_PARAM = 'lensView';

/**
 * The name this state used to travel under. It collided with the unified shell's own `view`
 * parameter: the shell wrote `view=lens` and this page then wrote its filters over the top of it,
 * or deleted it outright when the board sat at its defaults. Links kept working only because the
 * shell falls back to the Lens view whenever a lens id is present, so `?view=lensIndex` plus a
 * board filter was the losing case. Old links are still read from here.
 */
export const LEGACY_LENS_VIEW_PARAM = 'view';

const ENVELOPE_VERSION = 'v1';

export type LensUrlState = {
  filters: LensFilterValues;
  selections: LensSelection;
  /** Measure ranges the viewer dragged or typed, keyed by column. */
  ranges: Record<string, LensRange>;
};

/**
 * Whether the scope a link carried arrived whole.
 *
 * `shortened` is the case worth a sentence on screen: the address said it carried a scope, and it
 * did not survive the trip, so the recipient is looking at the board's default numbers under a
 * link that promised the sender's. Silently showing the default is the one outcome this journey
 * cannot allow.
 */
export type LensViewIntegrity = 'absent' | 'intact' | 'shortened';

const EMPTY: LensUrlState = { filters: {}, selections: {}, ranges: {} };

/** Links written before the period grammar carried the comparison as its own key. */
const LEGACY_COMPARISONS: Record<string, LensPeriodComparison> = {
  previousPeriod: 'previous',
  previousYear: { back: 1, unit: 'year' },
};

function isEmpty(value: Record<string, unknown>): boolean {
  return Object.keys(value).length === 0;
}

/**
 * Undoes what a chat client does to a bare URL.
 *
 * Slack renders a pasted link as `<url>` or `<url|label>` and a person writing "look at this:
 * <url>." leaves the trailing stop attached. None of that is part of the value, and all of it
 * arrives glued to the last parameter, which is this one.
 */
function unwrapPastedValue(raw: string): string {
  let value = raw.trim();
  if (value.startsWith('<')) {
    value = value.slice(1);
  }
  const labelAt = value.indexOf('|');
  if (labelAt >= 0) {
    value = value.slice(0, labelAt);
  }
  return value.replace(/[>).\],;]+$/, '');
}

/**
 * Returns undefined when there is nothing worth putting in the address, so a
 * board at its defaults keeps a clean URL rather than carrying `?lensView={}`.
 *
 * The value is stamped `v1.<bytes>.<json>` so a link that lost its tail on the way here can be
 * told apart from a link that never carried a scope. The stamp costs eight characters and is the
 * only thing standing between a truncated link and a reader reading the wrong numbers.
 */
export function encodeLensView(state: LensUrlState): string | undefined {
  const filters = state.filters ?? {};
  const selections = state.selections ?? {};
  const ranges = state.ranges ?? {};
  if (isEmpty(filters) && isEmpty(selections) && isEmpty(ranges)) {
    return undefined;
  }
  const body = JSON.stringify({
    ...(isEmpty(filters) ? {} : { f: filters }),
    ...(isEmpty(selections) ? {} : { s: selections }),
    ...(isEmpty(ranges) ? {} : { r: ranges }),
  });
  return `${ENVELOPE_VERSION}.${body.length}.${body}`;
}

/** Splits the stamp off a stamped value. Unstamped values are older links and carry no stamp. */
function readEnvelope(value: string): { body: string; declaredLength: number | null } {
  if (!value.startsWith(`${ENVELOPE_VERSION}.`)) {
    return { body: value, declaredLength: null };
  }
  const afterVersion = value.slice(ENVELOPE_VERSION.length + 1);
  const dot = afterVersion.indexOf('.');
  if (dot <= 0) {
    return { body: '', declaredLength: null };
  }
  const declared = Number(afterVersion.slice(0, dot));
  return {
    body: afterVersion.slice(dot + 1),
    declaredLength: Number.isInteger(declared) && declared >= 0 ? declared : null,
  };
}

/**
 * Says whether a link's scope arrived whole, without deciding what to do about it.
 *
 * A value with no stamp cannot be checked, so it is reported intact rather than accused: those are
 * links made before the stamp existed, and calling every one of them damaged would be worse than
 * saying nothing.
 */
export function lensViewIntegrity(raw: string | null | undefined): LensViewIntegrity {
  if (!raw?.trim()) {
    return 'absent';
  }
  const { body, declaredLength } = readEnvelope(unwrapPastedValue(raw));
  if (declaredLength === null) {
    // An unstamped value is either an older link or hand-written. Unparseable JSON is still
    // reportable: something was meant to be here and is not usable.
    if (!body.startsWith('{')) {
      return 'intact';
    }
    try {
      JSON.parse(body);
      return 'intact';
    } catch {
      return 'shortened';
    }
  }
  if (body.length !== declaredLength) {
    return 'shortened';
  }
  try {
    JSON.parse(body);
    return 'intact';
  } catch {
    return 'shortened';
  }
}

/**
 * Tolerant by design. A link can be truncated by a chat client, hand-edited, or
 * left over from an older shape of the board, and none of those should be an
 * error page: an unreadable view parameter just means the default view.
 *
 * {@link lensViewIntegrity} is what tells the reader it happened. This function's job is only to
 * keep the board on screen.
 */
export function decodeLensView(raw: string | null | undefined): LensUrlState {
  if (!raw) {
    return EMPTY;
  }
  const { body } = readEnvelope(unwrapPastedValue(raw));
  let parsed: unknown;
  try {
    parsed = JSON.parse(body);
  } catch {
    return EMPTY;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return EMPTY;
  }

  const source = parsed as { f?: unknown; s?: unknown; r?: unknown; c?: unknown };
  const filters: LensFilterValues = {};
  if (source.f && typeof source.f === 'object' && !Array.isArray(source.f)) {
    Object.entries(source.f as Record<string, unknown>).forEach(([key, value]) => {
      // A filter value is a string, a list of strings, or a date range object.
      // Anything else came from somewhere this code does not control.
      if (typeof value === 'string' || typeof value === 'boolean') {
        filters[key] = value;
      } else if (Array.isArray(value) && value.every((entry) => typeof entry === 'string')) {
        filters[key] = value;
      } else if (key === LENS_PERIOD_KEY && isLensPeriod(value)) {
        filters[key] = value as LensPeriod;
      } else if (value && typeof value === 'object') {
        if (Object.keys(value).length === 0 || ['min', 'max', 'from', 'to'].some((bound) => bound in value)) {
          filters[key] = value as LensFilterValues[string];
        }
      }
    });
  }

  const selections: LensSelection = {};
  if (source.s && typeof source.s === 'object' && !Array.isArray(source.s)) {
    Object.entries(source.s as Record<string, unknown>).forEach(([key, value]) => {
      if (Array.isArray(value) && value.every((entry) => typeof entry === 'string')) {
        selections[key] = value;
      }
    });
  }

  // A range narrows what a card shows just as much as a filter does, so a link that drops it
  // shows the recipient different numbers under the same address. Only finite numbers are taken:
  // a hand-edited bound of "abc" is not a bound.
  const ranges: Record<string, LensRange> = {};
  if (source.r && typeof source.r === 'object' && !Array.isArray(source.r)) {
    Object.entries(source.r as Record<string, unknown>).forEach(([key, value]) => {
      if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return;
      }
      const bound = value as { min?: unknown; max?: unknown };
      const range: LensRange = {
        ...(typeof bound.min === 'number' && Number.isFinite(bound.min) ? { min: bound.min } : {}),
        ...(typeof bound.max === 'number' && Number.isFinite(bound.max) ? { max: bound.max } : {}),
      };
      if (range.min !== undefined || range.max !== undefined) {
        ranges[key] = range;
      }
    });
  }

  const legacyComparison = typeof source.c === 'string' ? LEGACY_COMPARISONS[source.c] : undefined;
  if (legacyComparison && !(LENS_PERIOD_KEY in filters)) {
    filters[LENS_PERIOD_KEY] = { comparison: legacyComparison } as LensPeriod;
  }

  return { filters, selections, ranges };
}

/** True for a value that was written by this module rather than by the shell's own `view` param. */
function looksLikeLensView(value: string | null): boolean {
  if (!value) {
    return false;
  }
  const trimmed = unwrapPastedValue(value);
  return trimmed.startsWith(`${ENVELOPE_VERSION}.`) || trimmed.startsWith('{');
}

/** The raw scope value a search string carries, preferring the current name over the old one. */
export function lensViewParamValue(search: URLSearchParams | string): string | null {
  const params = typeof search === 'string' ? new URLSearchParams(search) : search;
  const current = params.get(LENS_VIEW_PARAM);
  if (current) {
    return current;
  }
  const legacy = params.get(LEGACY_LENS_VIEW_PARAM);
  return looksLikeLensView(legacy) ? legacy : null;
}

/** {@link decodeLensView} over whichever parameter the link actually used. */
export function readLensView(search: URLSearchParams | string): LensUrlState {
  return decodeLensView(lensViewParamValue(search));
}

/**
 * Applies the viewer's scope to a copy of `current`, leaving every other parameter alone.
 *
 * The legacy name is cleared only when it is holding a scope, so migrating an old link does not
 * take the unified shell's `view=lens` with it.
 */
export function applyLensView(current: URLSearchParams, state: LensUrlState): URLSearchParams {
  const next = new URLSearchParams(current);
  const encoded = encodeLensView(state);
  if (encoded) {
    next.set(LENS_VIEW_PARAM, encoded);
  } else {
    next.delete(LENS_VIEW_PARAM);
  }
  if (looksLikeLensView(next.get(LEGACY_LENS_VIEW_PARAM))) {
    next.delete(LEGACY_LENS_VIEW_PARAM);
  }
  return next;
}
